import { createFormContext } from '@mantine/form';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { getFetcher, putFetcher } from '@/api/fetchers';
import { useEffect } from 'react';
import { Button, CheckIcon, SimpleGrid, rem } from '@mantine/core';
import { MealVoucherCard } from './Cards/MealVoucherCard';
import { IdentityCard } from './Cards/IdentityCard';
import { notifications } from '@mantine/notifications';
import { isEmptyForm } from '@/utils/functions';
import { AttachmentCard } from './Cards/AttachmentCard';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { type EmployeeDetailed } from '@/schemas/API/GET/employee/response';
import {
  type EmployeeAttachments,
  type EmployeeAttachment,
} from '@/schemas/API/GET/employee/attachment/response';
import { type UpdateEmployee } from '@/schemas/API/PUT/employee/request';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { displayError } from '@/utils/error/errorHandler';

export const [EmployeeFormProvider, useEmployeeFormContext, useEmployeeForm] =
  createFormContext<EmployeeDetailed>();

interface IAttachmentForm {
  attachments: EmployeeAttachment[];
}

export const [
  AttachmentsFormProvider,
  useAttachmentsFormContext,
  useAttachmentsForm,
] = createFormContext<IAttachmentForm>();

export const EmployeeDocumentsSection: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();

  const { data: employee } = useSWR<EmployeeDetailed>(
    `/employees/${employeeId}`,
    getFetcher
  );

  const { data: attachments } = useSWR<EmployeeAttachments>(
    `/employees/${employeeId}/attachments`,
    getFetcher
  );

  const employeeForm = useEmployeeForm();
  const attachmentsForm = useAttachmentsForm();

  useEffect(() => {
    if (!employee || !attachments) return;

    employeeForm.setInitialValues(employee);
    employeeForm.setValues(employee);

    attachmentsForm.setFieldValue('attachments', attachments.content);
  }, [employee, attachments]);

  function fillAttributes(): UpdateEmployee {
    const tagIDs: number[] = employeeForm.values.tags.map(tag => tag.id);
    const localIDs: number[] = employeeForm.values.locs.map(
      location => location.id
    );
    const result: UpdateEmployee = {
      ...employeeForm.values,
      tags: tagIDs,
      locs: localIDs,
    };
    return result;
  }

  const updateDetails = async (): Promise<void> => {
    const updateEmployee = fillAttributes();

    try {
      await putFetcher(`employees/${employeeId}`, updateEmployee);
      notifications.show({
        message: 'Změny byly uloženy.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  if (!isEmptyForm(employeeForm))
    return (
      <EmployeeFormProvider form={employeeForm}>
        <AttachmentsFormProvider form={attachmentsForm}>
          <SimpleGrid cols={2}>
            <MealVoucherCard />
            <IdentityCard />
          </SimpleGrid>
          <AttachmentCard />
        </AttachmentsFormProvider>
        <ButtonBar>
          <Button
            radius="md"
            size="md"
            className="text-lg"
            onClick={updateDetails}
          >
            Uložit
          </Button>
        </ButtonBar>
      </EmployeeFormProvider>
    );
  return <SectionLoadingOverlay />;
};
